// Procedural screen backgrounds used when no AI key art is available (and as
// the base layer under it): a generic grid backdrop for menus, an animated
// night skyline for the title screen, and a hangar bay for mech selection.
// Everything is drawn once into canvas textures; animation is cheap
// tile-scrolling and a few tweened sprites. Randomness is purely cosmetic and
// seeded so the skyline looks the same every time.

import Phaser from 'phaser';
import { GAME_HEIGHT as H, GAME_WIDTH as W } from '../config/constants';
import { COLORS, css, mix } from '../config/palette';
import { Rng } from '../core/rng';
import { glowTexture } from './kit';

type Ctx = CanvasRenderingContext2D;

function canvasTexture(scene: Phaser.Scene, key: string, w: number, h: number, paint: (c: Ctx) => void): string {
  if (scene.textures.exists(key)) return key;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return '__DEFAULT';
  paint(tex.getContext());
  tex.refresh();
  return key;
}

// ───────────────────────────── Generic backdrop ─────────────────────────────

/** Dark gradient + fine grid + vignette. Returns the created objects (depth −100). */
export function gridBackdrop(scene: Phaser.Scene, accent: number = COLORS.volt): Phaser.GameObjects.GameObject[] {
  const key = canvasTexture(scene, 'uibg_grid', W, H, (c) => {
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#0b1019');
    g.addColorStop(0.6, '#070a10');
    g.addColorStop(1, '#040609');
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);
    c.strokeStyle = 'rgba(125,142,166,0.05)';
    c.lineWidth = 1;
    for (let x = 0; x <= W; x += 48) {
      c.beginPath();
      c.moveTo(x + 0.5, 0);
      c.lineTo(x + 0.5, H);
      c.stroke();
    }
    for (let y = 0; y <= H; y += 48) {
      c.beginPath();
      c.moveTo(0, y + 0.5);
      c.lineTo(W, y + 0.5);
      c.stroke();
    }
    const v = c.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 1.05);
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.75)');
    c.fillStyle = v;
    c.fillRect(0, 0, W, H);
  });
  const bg = scene.add.image(0, 0, key).setOrigin(0, 0).setDepth(-100);
  const glow = scene.add
    .image(W * 0.5, H * 0.42, glowTexture(scene))
    .setDisplaySize(W * 1.1, H * 0.9)
    .setTint(accent)
    .setAlpha(0.07)
    .setBlendMode(Phaser.BlendModes.ADD)
    .setDepth(-99);
  // Slow scan band.
  const scan = scene.add.rectangle(0, -120, W, 120, accent, 0.025).setOrigin(0, 0).setDepth(-98);
  scene.tweens.add({ targets: scan, y: H + 120, duration: 9000, repeat: -1, ease: 'Linear' });
  return [bg, glow, scan];
}

/** Cover-fit an image texture to the screen (keeps aspect, centred). */
export function coverImage(scene: Phaser.Scene, key: string): Phaser.GameObjects.Image {
  const img = scene.add.image(W / 2, H / 2, key);
  const s = Math.max(W / (img.width || 1), H / (img.height || 1));
  img.setScale(s);
  return img;
}

// ───────────────────────────── Title skyline ─────────────────────────────

interface LayerSpec {
  key: string;
  h: number;
  color: string;
  minH: number;
  maxH: number;
  lights: number;
  seed: number;
  antenna: number;
  signs: number;
}

const SKY_W = 1536;

function paintCityLayer(c: Ctx, s: LayerSpec): void {
  const rng = new Rng(s.seed);
  const lightCols = ['#ffd27a', '#7df9ff', '#ff4fd8', '#9fb0ff'];
  let x = -20;
  while (x < SKY_W + 20) {
    const bw = rng.int(48, 150);
    const bh = rng.int(s.minH, s.maxH);
    const top = s.h - bh;
    c.fillStyle = s.color;
    c.fillRect(x, top, bw, bh);
    // Stepped roofs / setbacks.
    if (rng.chance(0.5)) {
      const sw = Math.floor(bw * rng.float(0.4, 0.7));
      const sh = rng.int(14, 60);
      c.fillRect(x + Math.floor((bw - sw) / 2), top - sh, sw, sh);
    }
    if (rng.chance(s.antenna)) {
      c.fillRect(x + Math.floor(bw / 2), top - rng.int(30, 90), 2, 90);
    }
    // Window grid.
    const col = lightCols[rng.int(0, lightCols.length - 1)];
    for (let wy = top + 10; wy < s.h - 8; wy += 9) {
      for (let wx = x + 6; wx < x + bw - 6; wx += 7) {
        if (rng.chance(s.lights)) {
          c.globalAlpha = rng.float(0.25, 0.85);
          c.fillStyle = rng.chance(0.85) ? col : lightCols[rng.int(0, 3)];
          c.fillRect(wx, wy, 3, 4);
        }
      }
    }
    c.globalAlpha = 1;
    x += bw + rng.int(-10, 18);
  }
  // Neon signs: vertical glowing bars.
  for (let i = 0; i < s.signs; i++) {
    const sx = rng.int(20, SKY_W - 40);
    const sy = rng.int(Math.floor(s.h * 0.35), Math.floor(s.h * 0.75));
    const sh = rng.int(40, 110);
    const colr = rng.chance(0.5) ? '#ff3df2' : rng.chance(0.5) ? '#35e8ff' : '#ffb02e';
    c.shadowColor = colr;
    c.shadowBlur = 18;
    c.fillStyle = colr;
    c.fillRect(sx, sy, 10, sh);
    c.shadowBlur = 0;
    c.fillStyle = 'rgba(0,0,0,0.55)';
    for (let k = sy + 6; k < sy + sh - 4; k += 12) c.fillRect(sx + 2, k, 6, 5);
  }
}

/** Animated night megacity for the title screen. Call update(dt) every frame. */
export class Skyline {
  private readonly layers: { sprite: Phaser.GameObjects.TileSprite; speed: number }[] = [];
  private readonly beams: Phaser.GameObjects.Image[] = [];
  private t = 0;

  constructor(private readonly scene: Phaser.Scene) {
    const sky = canvasTexture(scene, 'uibg_sky', W, H, (c) => {
      const g = c.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#03050b');
      g.addColorStop(0.42, '#0a0f2a');
      g.addColorStop(0.68, '#251036');
      g.addColorStop(0.8, '#3a1238');
      g.addColorStop(1, '#0a0610');
      c.fillStyle = g;
      c.fillRect(0, 0, W, H);
      const rng = new Rng(911);
      for (let i = 0; i < 260; i++) {
        c.globalAlpha = rng.float(0.15, 0.8);
        c.fillStyle = '#dfe8ff';
        const s = rng.chance(0.08) ? 2 : 1;
        c.fillRect(rng.int(0, W), rng.int(0, Math.floor(H * 0.55)), s, s);
      }
      c.globalAlpha = 1;
      // Moon with halo.
      const mx = W * 0.74;
      const my = H * 0.27;
      const halo = c.createRadialGradient(mx, my, 60, mx, my, 420);
      halo.addColorStop(0, 'rgba(255,190,240,0.35)');
      halo.addColorStop(1, 'rgba(255,190,240,0)');
      c.fillStyle = halo;
      c.fillRect(0, 0, W, H);
      const moon = c.createRadialGradient(mx - 40, my - 40, 20, mx, my, 150);
      moon.addColorStop(0, '#fff1fb');
      moon.addColorStop(1, '#e6a6d8');
      c.fillStyle = moon;
      c.beginPath();
      c.arc(mx, my, 150, 0, Math.PI * 2);
      c.fill();
      // Cloud bands across the moon.
      c.fillStyle = 'rgba(16,10,34,0.75)';
      for (const [y, h, x0, w] of [[my + 20, 16, mx - 260, 420], [my + 58, 10, mx - 180, 380], [my - 40, 8, mx - 120, 300]]) {
        c.fillRect(x0, y, w, h);
      }
      // Horizon haze.
      const haze = c.createLinearGradient(0, H * 0.55, 0, H * 0.85);
      haze.addColorStop(0, 'rgba(255,61,242,0)');
      haze.addColorStop(1, 'rgba(255,61,242,0.18)');
      c.fillStyle = haze;
      c.fillRect(0, H * 0.55, W, H * 0.3);
    });
    scene.add.image(0, 0, sky).setOrigin(0, 0).setDepth(-100);

    // Searchlights behind the city.
    const beamKey = canvasTexture(scene, 'uibg_beam', 256, 1024, (c) => {
      const g = c.createLinearGradient(0, 1024, 0, 0);
      g.addColorStop(0, 'rgba(255,255,255,0.55)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = g;
      c.beginPath();
      c.moveTo(118, 1024);
      c.lineTo(138, 1024);
      c.lineTo(256, 0);
      c.lineTo(0, 0);
      c.closePath();
      c.fill();
    });
    for (const [x, tint, base] of [[W * 0.28, COLORS.volt, -0.35], [W * 0.62, COLORS.orbUnstable, 0.3]] as const) {
      const b = scene.add.image(x, H * 0.78, beamKey).setOrigin(0.5, 1).setTint(tint).setAlpha(0.13).setBlendMode(Phaser.BlendModes.ADD).setDepth(-97);
      b.setRotation(base);
      b.setData('base', base);
      this.beams.push(b);
    }

    const specs: (LayerSpec & { y: number; speed: number })[] = [
      { key: 'uibg_city_far', h: 520, color: '#170f2c', minH: 160, maxH: 420, lights: 0.1, seed: 11, antenna: 0.3, signs: 0, y: H * 0.82, speed: 6 },
      { key: 'uibg_city_mid', h: 560, color: '#0e0a1f', minH: 120, maxH: 460, lights: 0.16, seed: 23, antenna: 0.4, signs: 5, y: H * 0.9, speed: 14 },
      { key: 'uibg_city_near', h: 640, color: '#06050d', minH: 90, maxH: 520, lights: 0.12, seed: 37, antenna: 0.5, signs: 7, y: H + 20, speed: 30 },
    ];
    specs.forEach((s, i) => {
      const key = canvasTexture(scene, s.key, SKY_W, s.h, (c) => paintCityLayer(c, s));
      const sprite = scene.add.tileSprite(0, s.y, W, s.h, key).setOrigin(0, 1).setDepth(-96 + i * 2);
      this.layers.push({ sprite, speed: s.speed });
      if (i < 2) {
        // Fog between layers.
        const fog = canvasTexture(scene, `uibg_fog${i}`, 8, 256, (c) => {
          const g = c.createLinearGradient(0, 0, 0, 256);
          g.addColorStop(0, 'rgba(60,20,70,0)');
          g.addColorStop(1, `rgba(60,20,70,${0.55 - i * 0.15})`);
          c.fillStyle = g;
          c.fillRect(0, 0, 8, 256);
        });
        scene.add.image(0, s.y + 10, fog).setOrigin(0, 1).setDisplaySize(W, 300).setDepth(-95 + i * 2);
      }
    });

    // Ground reflection strip + rain.
    const ground = scene.add.graphics().setDepth(-90);
    ground.fillStyle(0x040309, 1);
    ground.fillRect(0, H - 60, W, 60);
    ground.fillStyle(COLORS.orbUnstable, 0.12);
    ground.fillRect(0, H - 60, W, 2);
    const rainKey = canvasTexture(scene, 'uibg_rain', 4, 48, (c) => {
      const g = c.createLinearGradient(0, 0, 0, 48);
      g.addColorStop(0, 'rgba(180,200,255,0)');
      g.addColorStop(1, 'rgba(180,200,255,0.7)');
      c.fillStyle = g;
      c.fillRect(1, 0, 2, 48);
    });
    scene.add
      .particles(0, -60, rainKey, {
        x: { min: -200, max: W + 200 },
        speedY: { min: 1300, max: 1700 },
        speedX: { min: -260, max: -200 },
        rotate: 9,
        lifespan: 1100,
        quantity: 2,
        frequency: 16,
        alpha: { min: 0.08, max: 0.28 },
        scaleY: { min: 0.6, max: 1.3 },
      })
      .setDepth(-89);
  }

  update(dt: number): void {
    this.t += dt;
    for (const l of this.layers) l.sprite.tilePositionX += l.speed * dt;
    this.beams.forEach((b, i) => {
      const base = b.getData('base') as number;
      b.setRotation(base + Math.sin(this.t * (0.18 + i * 0.07) + i * 2) * 0.32);
    });
  }
}

// ───────────────────────────── Hangar bay ─────────────────────────────

/** Static hangar interior (back wall, door, ceiling lights, perspective floor). */
export function hangarBay(scene: Phaser.Scene): Phaser.GameObjects.Image {
  const key = canvasTexture(scene, 'uibg_hangar', W, H, (c) => {
    const wall = c.createLinearGradient(0, 0, 0, H);
    wall.addColorStop(0, '#0a0d13');
    wall.addColorStop(0.7, '#121822');
    wall.addColorStop(1, '#06080c');
    c.fillStyle = wall;
    c.fillRect(0, 0, W, H);
    const horizon = 790;
    // Wall panels.
    for (let x = 0; x < W; x += 160) {
      c.fillStyle = x % 320 === 0 ? '#111720' : '#0e131b';
      c.fillRect(x, 90, 156, horizon - 90);
      c.fillStyle = 'rgba(255,255,255,0.025)';
      c.fillRect(x, 90, 156, 2);
      c.fillStyle = 'rgba(0,0,0,0.35)';
      c.fillRect(x + 156, 90, 4, horizon - 90);
      // Rivets / vents.
      c.fillStyle = 'rgba(125,142,166,0.10)';
      for (let y = 140; y < horizon - 40; y += 120) c.fillRect(x + 20, y, 116, 3);
    }
    // Hangar door behind the pedestal (left third).
    const dx = 210;
    const dw = 640;
    c.fillStyle = '#080b10';
    c.fillRect(dx, 150, dw, horizon - 150);
    for (let y = 160; y < horizon; y += 26) {
      c.fillStyle = '#10151d';
      c.fillRect(dx + 8, y, dw - 16, 20);
      c.fillStyle = 'rgba(255,255,255,0.03)';
      c.fillRect(dx + 8, y, dw - 16, 1);
    }
    c.strokeStyle = '#273140';
    c.lineWidth = 6;
    c.strokeRect(dx, 150, dw, horizon - 150);
    // Hazard stripes along the door frame bottom.
    for (let x = dx; x < dx + dw; x += 32) {
      c.fillStyle = '#c99a2e';
      c.beginPath();
      c.moveTo(x, horizon - 18);
      c.lineTo(x + 16, horizon - 18);
      c.lineTo(x + 32, horizon);
      c.lineTo(x + 16, horizon);
      c.closePath();
      c.fill();
    }
    // Ceiling truss + lights.
    c.fillStyle = '#05070a';
    c.fillRect(0, 0, W, 90);
    for (let x = 40; x < W; x += 240) {
      c.fillStyle = '#1b2230';
      c.fillRect(x, 70, 160, 10);
      c.fillStyle = '#dfe8f5';
      c.fillRect(x + 8, 78, 144, 4);
      const cone = c.createLinearGradient(0, 82, 0, 520);
      cone.addColorStop(0, 'rgba(200,220,255,0.10)');
      cone.addColorStop(1, 'rgba(200,220,255,0)');
      c.fillStyle = cone;
      c.beginPath();
      c.moveTo(x + 8, 82);
      c.lineTo(x + 152, 82);
      c.lineTo(x + 230, 520);
      c.lineTo(x - 70, 520);
      c.closePath();
      c.fill();
    }
    // Floor with perspective grid.
    const floor = c.createLinearGradient(0, horizon, 0, H);
    floor.addColorStop(0, '#141b25');
    floor.addColorStop(1, '#07090d');
    c.fillStyle = floor;
    c.fillRect(0, horizon, W, H - horizon);
    c.strokeStyle = 'rgba(125,142,166,0.10)';
    c.lineWidth = 1;
    const vx = W * 0.3;
    for (let i = -24; i <= 24; i++) {
      c.beginPath();
      c.moveTo(vx + i * 40, horizon);
      c.lineTo(vx + i * 260, H);
      c.stroke();
    }
    for (let k = 0; k < 9; k++) {
      const y = horizon + Math.pow(k / 8, 2.1) * (H - horizon);
      c.beginPath();
      c.moveTo(0, y);
      c.lineTo(W, y);
      c.stroke();
    }
    c.fillStyle = 'rgba(255,255,255,0.05)';
    c.fillRect(0, horizon, W, 2);
    const v = c.createRadialGradient(W * 0.35, H * 0.5, H * 0.25, W * 0.5, H * 0.5, H * 1.1);
    v.addColorStop(0, 'rgba(0,0,0,0)');
    v.addColorStop(1, 'rgba(0,0,0,0.7)');
    c.fillStyle = v;
    c.fillRect(0, 0, W, H);
  });
  return scene.add.image(0, 0, key).setOrigin(0, 0).setDepth(-100);
}

/** Tinted light beam + glow used behind a showcased mech. */
export function spotlight(scene: Phaser.Scene, x: number, floorY: number, color: number): { beam: Phaser.GameObjects.Image; glow: Phaser.GameObjects.Image } {
  const key = canvasTexture(scene, 'uibg_spot', 256, 512, (c) => {
    const g = c.createLinearGradient(0, 0, 0, 512);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.4)');
    g.addColorStop(1, 'rgba(255,255,255,0.75)');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(96, 0);
    c.lineTo(160, 0);
    c.lineTo(256, 512);
    c.lineTo(0, 512);
    c.closePath();
    c.fill();
  });
  const beam = scene.add.image(x, floorY, key).setOrigin(0.5, 1).setDisplaySize(560, 900).setTint(color).setAlpha(0.11).setBlendMode(Phaser.BlendModes.ADD);
  const glow = scene.add.image(x, floorY - 170, glowTexture(scene)).setDisplaySize(720, 620).setTint(color).setAlpha(0.22).setBlendMode(Phaser.BlendModes.ADD);
  return { beam, glow };
}

/** Lighter version of a colour for text on dark panels. */
export function lighten(color: number, t = 0.35): number {
  return mix(color, 0xffffff, t);
}

export { css };
